import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { Newspaper } from 'lucide-react';
import { PasswordResetModal } from '../components/PasswordResetModal.js';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      // Index route picks the right landing per the operator's roles.
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-grey-01 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-surface rounded-lg border border-grey-02 p-8">
        <div className="flex flex-col items-center mb-8">
          <div className="bg-primary-50 p-3 rounded-lg mb-4">
            <img src="/apple-touch-icon.png" alt="Assayer Network" className="h-12 w-12 rounded-xl" />
          </div>
          <h1 className="text-3xl font-bold text-grey-07">Assayer Intelligence</h1>
          <p className="text-grey-05 mt-2">Sign in to your account</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg">
            <p className="text-danger-600 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-grey-07 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border border-grey-02 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-grey-07 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 border border-grey-02 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-primary-04 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setResetOpen(true)}
              className="text-sm text-primary-04 hover:text-primary-600 font-medium"
            >
              Forgot password?
            </button>
          </div>
        </form>
      </div>

      {resetOpen && (
        <PasswordResetModal onClose={() => setResetOpen(false)} initialEmail={email} />
      )}
    </div>
  );
}
